import { useState, useRef } from "react";
import { isSuperAdmin } from "@shared/roles";
import { useSearch } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useUpdateProfile, useUploadAvatar } from "@/hooks/use-profiles";
import { useAgences, useCreateAgence, useUpdateAgence, useDeleteAgence } from "@/hooks/use-agences";
import { useTheme } from "@/contexts/theme-context";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VaultModal } from "@/components/vault-modal";
import EquipeList from "@/pages/equipe/list";
import IntegrationsList from "@/pages/integrations/list";
import {
  User, Users, Plug, Lock, Palette, Camera, Loader2,
  Sun, Moon, Monitor, KeyRound, Wrench, Download, FileSpreadsheet,
  ShieldCheck, ChevronLeft, ChevronRight, FileDown, Sparkles, Building2, Plus, Pencil, Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { authFetch } from "@/lib/authFetch";
import { ChangelogAdminPanel } from "@/components/changelog-drawer";

const TABS = [
  { id: "profil",       label: "Mon profil",   icon: User },
  { id: "equipe",       label: "Équipe",        icon: Users },
  { id: "integrations", label: "Intégrations",  icon: Plug },
  { id: "coffre-fort",  label: "Coffre-fort",   icon: Lock },
  { id: "apparence",    label: "Apparence",      icon: Palette },
  { id: "outils",       label: "Outils",         icon: Wrench, superAdminOnly: true },
  { id: "audit",        label: "Journal d'audit", icon: ShieldCheck, superAdminOnly: true },
  { id: "changelog",    label: "Changelog",       icon: Sparkles,    superAdminOnly: true },
  { id: "agences",      label: "Agences",         icon: Building2,   superAdminOnly: true },
];

const ROLE_LABELS: Record<string, string> = {
  admin: "Administrateur",
  planification: "Planification",
  technicien: "Technicien",
  commercial: "Commercial",
  administration: "Administration",
};

// ── Onglet : Mon Profil ───────────────────────────────────────────────────────

function ProfileTab() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState(profile?.firstName || "");
  const [lastName, setLastName] = useState(profile?.lastName || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSave = async () => {
    if (!profile?.id) return;
    try {
      await updateProfile.mutateAsync({ id: profile.id, firstName, lastName, phone });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Profil mis à jour" });
    } catch {
      toast({ title: "Erreur lors de la sauvegarde", variant: "destructive" });
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.id) return;
    try {
      await uploadAvatar.mutateAsync({ profileId: profile.id, file });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Avatar mis à jour" });
    } catch {
      toast({ title: "Erreur lors du chargement", variant: "destructive" });
    }
  };

  const handlePasswordReset = async () => {
    const email = profile?.email;
    if (!email) {
      toast({ title: "Email non disponible", description: "Aucun email associé à ce compte.", variant: "destructive" });
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setResetSent(true);
      toast({ title: "Email envoyé", description: `Un lien de réinitialisation a été envoyé à ${email}` });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="max-w-xl space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-1">Mon profil</h2>
        <p className="text-sm text-muted-foreground">Gérez vos informations personnelles et votre mot de passe.</p>
      </div>

      {/* Avatar */}
      <div className="flex items-center gap-6">
        <div className="relative group">
          <Avatar className="h-20 w-20 border-2 border-background shadow-md">
            <AvatarImage src={profile?.avatarUrl || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary font-bold text-2xl">
              {profile?.firstName?.[0]}{profile?.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <button
            onClick={() => avatarInputRef.current?.click()}
            className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="button-change-avatar"
          >
            {uploadAvatar.isPending ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <Camera className="w-5 h-5 text-white" />
            )}
          </button>
          <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
        </div>
        <div>
          <p className="font-semibold text-lg">{profile?.firstName} {profile?.lastName}</p>
          <Badge variant="secondary" className="mt-1 text-xs">
            {ROLE_LABELS[profile?.role || ""] || profile?.role}
          </Badge>
          {profile?.email && (
            <p className="text-sm text-muted-foreground mt-1">{profile.email}</p>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="firstName">Prénom</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              data-testid="input-firstName"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName">Nom</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              data-testid="input-lastName"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Téléphone</Label>
          <Input
            id="phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="+33 6 00 00 00 00"
            data-testid="input-phone"
          />
        </div>
        <Button onClick={handleSave} disabled={updateProfile.isPending} data-testid="button-save-profile">
          {updateProfile.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Enregistrer
        </Button>
      </div>

      {/* Password reset */}
      <div className="border-t pt-6 space-y-3">
        <h3 className="font-medium">Sécurité</h3>
        <p className="text-sm text-muted-foreground">
          Recevoir un lien de réinitialisation de mot de passe par email.
        </p>
        <Button
          variant="outline"
          onClick={handlePasswordReset}
          disabled={resetLoading || resetSent}
          data-testid="button-reset-password"
        >
          {resetLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
          {resetSent ? "Email envoyé ✓" : "Changer mon mot de passe"}
        </Button>
      </div>
    </div>
  );
}

// ── Onglet : Coffre-fort ──────────────────────────────────────────────────────

function VaultTab() {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-1">Coffre-fort</h2>
        <p className="text-sm text-muted-foreground">Accès sécurisé aux mots de passe et outils de l'équipe. Protégé par code PIN.</p>
      </div>
      <div className="bg-secondary/30 rounded-xl p-8 flex flex-col items-center gap-4 border border-border/50">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Lock className="w-8 h-8 text-primary" />
        </div>
        <p className="text-sm text-muted-foreground text-center">Cliquez sur le bouton ci-dessous pour accéder au coffre-fort.<br />Un code PIN à 6 chiffres vous sera demandé.</p>
        <Button onClick={() => setIsOpen(true)} data-testid="button-open-vault-settings" className="gap-2">
          <Lock className="w-4 h-4" />
          Ouvrir le coffre-fort
        </Button>
      </div>
      <VaultModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  );
}

// ── Onglet : Apparence ────────────────────────────────────────────────────────

function AppearanceTab() {
  const { theme, setTheme } = useTheme();

  const options: { value: "system" | "light" | "dark"; label: string; icon: typeof Sun; desc: string }[] = [
    { value: "system", label: "Système",  icon: Monitor, desc: "Suit les préférences de votre système d'exploitation" },
    { value: "light",  label: "Clair",    icon: Sun,     desc: "Thème clair, fond blanc" },
    { value: "dark",   label: "Sombre",   icon: Moon,    desc: "Thème sombre, réduit la fatigue oculaire" },
  ];

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-1">Apparence</h2>
        <p className="text-sm text-muted-foreground">Choisissez le thème de l'interface. La préférence est sauvegardée localement.</p>
      </div>
      <div className="grid gap-3">
        {options.map(opt => {
          const Icon = opt.icon;
          const active = theme === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setTheme(opt.value)}
              data-testid={`button-theme-${opt.value}`}
              className={cn(
                "flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all",
                active
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border bg-card hover:border-primary/40 hover:bg-secondary/30 text-foreground"
              )}
            >
              <div className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                active ? "bg-primary/15" : "bg-secondary"
              )}>
                <Icon className={cn("w-5 h-5", active ? "text-primary" : "text-muted-foreground")} />
              </div>
              <div className="flex-1">
                <p className="font-medium">{opt.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
              </div>
              {active && (
                <div className="w-2.5 h-2.5 rounded-full bg-primary shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Onglet : Outils ───────────────────────────────────────────────────────────

function OutilsTab() {
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const { toast } = useToast();

  async function downloadCsv(url: string, filename: string, setLoading: (v: boolean) => void) {
    setLoading(true);
    try {
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Erreur serveur");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch {
      toast({ title: "Erreur lors du téléchargement", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-1">Outils</h2>
        <p className="text-sm text-muted-foreground">Utilitaires d'administration temporaires.</p>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Export écarts numérotation clients</h3>

        <div className="border border-border rounded-xl divide-y divide-border overflow-hidden">
          {/* Fichier A */}
          <div className="flex items-start gap-4 p-5">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <FileSpreadsheet className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">Fichier A — Clients sans numéro</p>
              <p className="text-xs text-muted-foreground mt-1">
                Liste des clients en base dont le champ <code className="bg-secondary px-1 rounded text-[11px]">numero_client</code> est NULL après le script d'attribution.
                Colonnes : id, nom, nom_commercial, adresse, date_création.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="btn-download-clients-sans-numero"
              disabled={loadingA}
              onClick={() => downloadCsv("/api/export/clients-sans-numero", "clients_sans_numero.csv", setLoadingA)}
              className="shrink-0"
            >
              {loadingA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="ml-2">Télécharger</span>
            </Button>
          </div>

          {/* Fichier B */}
          <div className="flex items-start gap-4 p-5">
            <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <FileSpreadsheet className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">Fichier B — Entrées CSV sans correspondance</p>
              <p className="text-xs text-muted-foreground mt-1">
                Lignes du CSV de référence qui n'ont pas matché de client en base, avec 3 suggestions de correspondance (informatives, aucune mise à jour BDD).
                Colonnes : numero_client, nom_dossier, suggestion_1, suggestion_2, suggestion_3.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="btn-download-csv-sans-correspondance"
              disabled={loadingB}
              onClick={() => downloadCsv("/api/export/csv-sans-correspondance", "csv_sans_correspondance.csv", setLoadingB)}
              className="shrink-0"
            >
              {loadingB ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="ml-2">Télécharger</span>
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground italic">
          Cet outil est temporaire — il peut être retiré une fois le chantier de numérotation clôturé.
        </p>
      </div>
    </div>
  );
}

// ── Onglet : Journal d'audit ──────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  access_role_change: "Changement de rôle",
  task_deleted:       "Suppression de tâche",
  vault_access:       "Accès coffre-fort",
  invitation_sent:    "Invitation envoyée",
};

const ACTION_COLORS: Record<string, string> = {
  access_role_change: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  task_deleted:       "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  vault_access:       "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  invitation_sent:    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
};

function AuditTab() {
  const [filterAction, setFilterAction] = useState("all");
  const [filterUserId, setFilterUserId] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const PAGE_SIZE = 50;

  const { data: profilesData } = useQuery<any[]>({
    queryKey: ["/api/profiles"],
    queryFn: () => authFetch("/api/profiles").then(r => r.json()),
  });
  const profileOptions = profilesData ?? [];

  const queryParams = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
  if (filterAction !== "all") queryParams.set("action", filterAction);
  if (filterUserId !== "all") queryParams.set("userId", filterUserId);
  if (filterDateFrom) queryParams.set("dateFrom", filterDateFrom);
  if (filterDateTo) queryParams.set("dateTo", new Date(new Date(filterDateTo).getTime() + 86400000).toISOString());

  const { data, isLoading } = useQuery<{ rows: any[]; total: number }>({
    queryKey: ["/api/audit-logs", filterAction, filterUserId, filterDateFrom, filterDateTo, page],
    queryFn: () => authFetch(`/api/audit-logs?${queryParams}`).then(r => r.json()),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const hasFilters = filterAction !== "all" || filterUserId !== "all" || filterDateFrom || filterDateTo;

  function resetFilters() {
    setFilterAction("all");
    setFilterUserId("all");
    setFilterDateFrom("");
    setFilterDateTo("");
    setPage(0);
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const exportParams = new URLSearchParams();
      if (filterAction !== "all") exportParams.set("action", filterAction);
      if (filterUserId !== "all") exportParams.set("userId", filterUserId);
      if (filterDateFrom) exportParams.set("dateFrom", filterDateFrom);
      if (filterDateTo) exportParams.set("dateTo", new Date(new Date(filterDateTo).getTime() + 86400000).toISOString());
      const response = await authFetch(`/api/audit-logs/export?${exportParams}`);
      if (!response.ok) throw new Error("Erreur lors de l'export");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will notice no download occurred
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold mb-1">Journal d'audit</h2>
          <p className="text-sm text-muted-foreground">Traçabilité des actions sensibles : connexions au coffre-fort, suppressions, changements de rôle et invitations.</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting}
          className="shrink-0" data-testid="button-audit-export">
          {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          Exporter CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Type d'action</Label>
          <Select value={filterAction} onValueChange={v => { setFilterAction(v); setPage(0); }}>
            <SelectTrigger className="h-8 text-sm w-48" data-testid="select-audit-action">
              <SelectValue placeholder="Toutes les actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les actions</SelectItem>
              <SelectItem value="access_role_change">Changement de rôle</SelectItem>
              <SelectItem value="task_deleted">Suppression de tâche</SelectItem>
              <SelectItem value="vault_access">Accès coffre-fort</SelectItem>
              <SelectItem value="invitation_sent">Invitation envoyée</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Utilisateur</Label>
          <Select value={filterUserId} onValueChange={v => { setFilterUserId(v); setPage(0); }}>
            <SelectTrigger className="h-8 text-sm w-44" data-testid="select-audit-user">
              <SelectValue placeholder="Tous les utilisateurs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les utilisateurs</SelectItem>
              {profileOptions.map((p: any) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.firstName} {p.lastName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Du</Label>
          <Input type="date" className="h-8 text-sm w-36" value={filterDateFrom}
            onChange={e => { setFilterDateFrom(e.target.value); setPage(0); }}
            data-testid="input-audit-date-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Au</Label>
          <Input type="date" className="h-8 text-sm w-36" value={filterDateTo}
            onChange={e => { setFilterDateTo(e.target.value); setPage(0); }}
            data-testid="input-audit-date-to" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={resetFilters}
            data-testid="button-audit-reset">
            Réinitialiser
          </Button>
        )}
        <span className="ml-auto text-xs text-muted-foreground pt-5">{total} événement{total !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 border-b border-border">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Date</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Utilisateur</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Action</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Détails</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr><td colSpan={4} className="text-center py-10 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-10 text-muted-foreground text-sm">
                Aucun événement trouvé
              </td></tr>
            ) : rows.map((row: any) => (
              <tr key={row.id} className="hover:bg-muted/20 transition-colors" data-testid={`row-audit-${row.id}`}>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(row.createdAt)}</td>
                <td className="px-4 py-3">
                  {row.userFirstName ? (
                    <div>
                      <p className="font-medium text-xs">{row.userFirstName} {row.userLastName}</p>
                      <p className="text-xs text-muted-foreground">{row.userEmail}</p>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Utilisateur supprimé</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", ACTION_COLORS[row.action] || "bg-secondary text-foreground")}>
                    {ACTION_LABELS[row.action] || row.action}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs">
                  {row.action === 'access_role_change' && row.metadata ? (
                    <span>
                      {row.metadata.targetEmail} : <span className="font-mono">{row.metadata.previousAccessRole}</span> → <span className="font-mono font-semibold text-foreground">{row.metadata.newAccessRole}</span>
                    </span>
                  ) : row.action === 'invitation_sent' && row.metadata ? (
                    <span>{row.metadata.targetName} ({row.metadata.targetEmail})</span>
                  ) : row.action === 'task_deleted' ? (
                    <span className="font-mono">{row.entityId}</span>
                  ) : row.action === 'vault_access' ? (
                    <span>Accès réussi</span>
                  ) : (
                    <span>{row.entityType} {row.entityId}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="button-audit-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </Button>
          <span className="text-muted-foreground text-xs">Page {page + 1} / {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="button-audit-next">
            Suivant <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

export function AgencesTab() {
  const { data: agences, isLoading, isError, refetch } = useAgences();
  const createAgence = useCreateAgence();
  const updateAgence = useUpdateAgence();
  const deleteAgence = useDeleteAgence();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [ville, setVille] = useState("");
  const [googleCalendarId, setGoogleCalendarId] = useState("");
  const [actif, setActif] = useState(true);

  const beginCreate = () => {
    setEditingId(null); setCode(""); setLibelle(""); setVille(""); setGoogleCalendarId(""); setActif(true); setOpen(true);
  };
  const beginEdit = (agence: any) => {
    setEditingId(agence.id); setCode(agence.code); setLibelle(agence.libelle);
    setVille(agence.ville || ""); setGoogleCalendarId(agence.googleCalendarId || ""); setActif(agence.actif); setOpen(true);
  };
  const save = async () => {
    if (!libelle.trim() || (!editingId && !code.trim())) {
      toast({ title: "Champs requis", description: "Le code et le libellé sont requis.", variant: "destructive" }); return;
    }
    try {
      const data = { code: code.trim(), libelle: libelle.trim(), ville: ville.trim() || null, googleCalendarId: googleCalendarId.trim() || null, actif };
      if (editingId) await updateAgence.mutateAsync({ id: editingId, ...data });
      else await createAgence.mutateAsync(data);
      setOpen(false); toast({ title: editingId ? "Agence modifiée" : "Agence créée" });
    } catch (e: any) { toast({ title: "Erreur", description: e.message, variant: "destructive" }); }
  };
  const remove = async (id: string) => {
    if (!window.confirm("Supprimer cette agence ?")) return;
    try { await deleteAgence.mutateAsync(id); toast({ title: "Agence supprimée" }); }
    catch (e: any) { toast({ title: "Suppression impossible", description: e.message, variant: "destructive" }); }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div><h2 className="text-xl font-semibold mb-1">Agences</h2><p className="text-sm text-muted-foreground">Gérez les rattachements et les calendriers par agence.</p></div>
        <Button onClick={beginCreate} data-testid="button-create-agence"><Plus className="w-4 h-4 mr-2" />Nouvelle agence</Button>
      </div>
      {isError ? <div className="rounded-lg border border-destructive/30 p-5 text-sm text-destructive">Impossible de charger les agences. <Button variant="ghost" onClick={() => refetch()}>Réessayer</Button></div> :
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm"><thead><tr className="bg-muted/40 border-b">
            <th className="text-left px-4 py-3 font-medium">Code</th><th className="text-left px-4 py-3 font-medium">Agence</th><th className="text-left px-4 py-3 font-medium">Ville</th><th className="text-left px-4 py-3 font-medium">Calendrier Google</th><th className="text-left px-4 py-3 font-medium">Statut</th><th className="w-24" />
          </tr></thead><tbody className="divide-y divide-border">
            {isLoading ? <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">Chargement...</td></tr> :
              (agences || []).length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">Aucune agence</td></tr> :
              agences?.map(agence => <tr key={agence.id} data-testid={`row-agence-${agence.id}`} className="hover:bg-muted/20">
                <td className="px-4 py-3 font-mono text-xs">{agence.code}</td><td className="px-4 py-3 font-medium">{agence.libelle}</td><td className="px-4 py-3 text-muted-foreground">{agence.ville || "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground max-w-[220px] truncate">{agence.googleCalendarId || "—"}</td>
                <td className="px-4 py-3"><Badge variant={agence.actif ? "default" : "secondary"}>{agence.actif ? "Active" : "Inactive"}</Badge></td>
                <td className="px-2 py-3"><div className="flex justify-end gap-1"><Button variant="ghost" size="icon" onClick={() => beginEdit(agence)} aria-label="Modifier"><Pencil className="w-4 h-4" /></Button><Button variant="ghost" size="icon" onClick={() => remove(agence.id)} aria-label="Supprimer"><Trash2 className="w-4 h-4 text-destructive" /></Button></div></td>
              </tr>)}
          </tbody></table>
        </div>}
      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editingId ? "Modifier l'agence" : "Nouvelle agence"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4"><div className="space-y-1.5"><Label>Code *</Label><Input value={code} disabled={!!editingId} onChange={e => setCode(e.target.value)} placeholder="PAR" /></div><div className="space-y-1.5"><Label>Libellé *</Label><Input value={libelle} onChange={e => setLibelle(e.target.value)} placeholder="Paris" /></div></div>
          <div className="space-y-1.5"><Label>Ville</Label><Input value={ville} onChange={e => setVille(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>ID Google Calendar</Label><Input value={googleCalendarId} onChange={e => setGoogleCalendarId(e.target.value)} /></div>
          <div className="flex items-center justify-between rounded-lg border p-3"><div><Label>Agence active</Label><p className="text-xs text-muted-foreground">Disponible pour les nouveaux rattachements</p></div><Switch checked={actif} onCheckedChange={setActif} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={save} disabled={createAgence.isPending || updateAgence.isPending}>Enregistrer</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ── Page Paramètres ───────────────────────────────────────────────────────────

export default function ParametresList() {
  const { profile } = useAuth();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const defaultTab = params.get("onglet") || "profil";
  const [activeTab, setActiveTab] = useState(
    TABS.find(t => t.id === defaultTab) ? defaultTab : "profil"
  );

  const visibleTabs = TABS.filter(t => {
    if ((t as any).superAdminOnly) return isSuperAdmin(profile?.accessRole);
    return true;
  });

  const renderContent = () => {
    switch (activeTab) {
      case "profil":       return <ProfileTab />;
      case "equipe":       return <EquipeList />;
      case "integrations": return <IntegrationsList />;
      case "coffre-fort":  return <VaultTab />;
      case "apparence":    return <AppearanceTab />;
      case "outils":       return <OutilsTab />;
      case "audit":        return <AuditTab />;
      case "changelog":    return <ChangelogAdminPanel />;
      case "agences":      return <AgencesTab />;
      default:             return <ProfileTab />;
    }
  };

  return (
    <div className="flex gap-0 min-h-[calc(100vh-8rem)]">
      {/* Sidebar des onglets */}
      <nav className="w-52 shrink-0 pr-6 border-r border-border/50">
        <h1 className="text-lg font-bold mb-6 px-3 pt-1">Paramètres</h1>
        <div className="space-y-0.5">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-parametres-${tab.id}`}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
              >
                <Icon className={cn("w-4 h-4 shrink-0", active ? "text-primary" : "")} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Contenu */}
      <div className="flex-1 pl-8 pt-1">
        {renderContent()}
      </div>
    </div>
  );
}
